import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
// components
import LoadingScreen from '../components/LoadingScreen';
// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  const isDashboard = pathname.includes('/dashboard');

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed'
            })
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
        },
        { path: 'logout', element: <Logout /> }
      ]
    },
    // Dashboard Routes
    {
      path: 'admin',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/admin/product" replace /> },
        { path: 'product', element: <Product /> },
        { path: 'users', element: <Users /> },
        { path: 'branch', element: <Branch /> },
        { path: 'send-message', element: <SendMessage /> },
        { path: 'send-email', element: <SendEmail /> },
        {
          path: 'report',
          children: [
            { element: <Navigate to="/report/product" replace /> },
            { path: 'product', element: <ReportProduct /> },
            { path: 'product-summary', element: <ReportProductSummary /> }
          ]
        },
        {
          path: 'settings',
          children: [{ element: <Navigate to="/settings/email" replace /> }, { path: 'email', element: <Settings /> }]
        }
      ]
    },
    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    },
    {
      path: '/',
      children: [
        {
          index: true,
          element: <Navigate replace to="/admin" />
        }
      ]
    },
    { path: '*', element: <Navigate to="/404" replace /> }
  ]);
}

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('../pages/authentication/Login')));
// Dashboard
const Branch = Loadable(lazy(() => import('../pages/Branch')));
const Product = Loadable(lazy(() => import('../pages/Products')));
const ReportProduct = Loadable(lazy(() => import('../pages/ReportProduct')));
const ReportProductSummary = Loadable(lazy(() => import('../pages/ReportProductSummary')));
const Settings = Loadable(lazy(() => import('../pages/Settings')));
const Users = Loadable(lazy(() => import('../pages/Users')));
const Logout = Loadable(lazy(() => import('../pages/Logout')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
//
const SendMessage = Loadable(lazy(() => import('../pages/SendMessage')));
const SendEmail = Loadable(lazy(() => import('../pages/SendEmail')));
// Main
const LandingPage = Loadable(lazy(() => import('../pages/LandingPage')));
